import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';

/**
 * Dark horizontal overlay wipe with gold accent.
 *
 * On every route change, a full-bleed dark panel sweeps left-to-right across the screen.
 * A 3px gold line glows on the leading edge of the panel, evoking a luxe "curtain cut".
 * Total duration ~750ms; uses easeInOut so the wipe feels weighty, not jittery.
 */
const RouteWipe = () => {
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);
  const [wipeId, setWipeId] = useState(0);

  useEffect(() => {
    if (location.pathname !== prevPathRef.current) {
      prevPathRef.current = location.pathname;
      setWipeId((n) => n + 1);
    }
  }, [location]);

  if (wipeId === 0) return null;

  return (
    <AnimatePresence>
      <motion.div
        key={wipeId}
        className="fixed inset-0 z-[9998] pointer-events-none"
        initial={{ x: '-101%' }}
        animate={{ x: '101%' }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.75, ease: [0.76, 0, 0.24, 1] }}
        data-testid="route-wipe"
      >
        {/* Dark slab */}
        <div className="absolute inset-0 bg-[#0e0b04]" />

        {/* Subtle grain texture to avoid flat-black look */}
        <div
          className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='2.2' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
            backgroundSize: '180px 180px',
          }}
        />

        {/* Soft inner gold gradient — adds depth */}
        <div
          className="absolute inset-y-0 right-0 w-[28vw]"
          style={{
            background:
              'linear-gradient(to right, rgba(203,161,53,0) 0%, rgba(203,161,53,0.04) 60%, rgba(230,190,75,0.10) 100%)',
          }}
        />

        {/* Gold leading-edge accent (right side of the moving panel) */}
        <div
          className="absolute top-0 right-0 h-full w-[3px]"
          style={{
            background:
              'linear-gradient(to bottom, transparent 0%, rgba(203,161,53,0.25) 12%, #e6be4b 50%, rgba(203,161,53,0.25) 88%, transparent 100%)',
            boxShadow:
              '0 0 12px rgba(230, 190, 75, 0.85), 0 0 28px rgba(203, 161, 53, 0.55), 0 0 60px rgba(203, 161, 53, 0.25)',
          }}
        />

        {/* Wider soft glow halo behind the edge — gives the "swoosh" feel */}
        <div
          className="absolute top-0 right-0 h-full w-[60px]"
          style={{
            background:
              'linear-gradient(to left, rgba(230, 190, 75, 0.18), rgba(203, 161, 53, 0.06) 40%, transparent 100%)',
            filter: 'blur(6px)',
          }}
        />
      </motion.div>
    </AnimatePresence>
  );
};

export default RouteWipe;
